"use client";

import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Inset,
  Separator,
  Text,
} from "@radix-ui/themes";
import FontAwesome from "../../components/utilities/FontAwesome";

type PageIndexSelectedEntry = {
  id: string;
  title: string;
};

type PageIndexBulkBarProps = {
  selectedCount: number;
  selectedEntries: PageIndexSelectedEntry[];
  hasSelection: boolean;
  bulkPending: boolean;
  isMixedSelection: boolean;
  publishedSelectedCount: number;
  onClear: () => void;
  onExit: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDeleteRequest: () => void;
  onRemoveSelected: (id: string) => void;
};

export default function PageIndexBulkBar({
  selectedCount,
  selectedEntries,
  hasSelection,
  bulkPending,
  isMixedSelection,
  publishedSelectedCount,
  onClear,
  onExit,
  onPublish,
  onUnpublish,
  onDeleteRequest,
  onRemoveSelected,
}: PageIndexBulkBarProps) {
  const unpublishedSelectedCount = selectedCount - publishedSelectedCount;

  return (
    <Card
      variant="surface"
      size="1"
      mb="4"
      style={{ borderColor: "var(--accent-8)" }}
    >
      <Flex align="center" gap="2" wrap="wrap" pt="1" px="1" pb="3">
        <Flex align="center" gap="2" wrap="wrap">
          <Button
            variant="surface"
            color="gray"
            disabled={!hasSelection || bulkPending}
            onClick={onPublish}
            style={{ opacity: hasSelection ? 1 : 0.45 }}
          >
            <FontAwesome
              prefix="fas"
              name="fa-circle-check"
              ariaHidden
              style={{ color: "var(--green-11)" }}
            />
            Publish
          </Button>
          <Button
            variant="surface"
            color="gray"
            disabled={!hasSelection || bulkPending}
            onClick={onUnpublish}
            style={{ opacity: hasSelection ? 1 : 0.45 }}
          >
            <FontAwesome
              prefix="fas"
              name="fa-circle-minus"
              ariaHidden
              style={{ color: "var(--amber-11)" }}
            />
            Unpublish
          </Button>
          <Button
            variant="surface"
            color="red"
            disabled={!hasSelection || bulkPending}
            onClick={onDeleteRequest}
            style={{ opacity: hasSelection ? 1 : 0.45 }}
          >
            <FontAwesome prefix="fas" name="fa-trash" ariaHidden />
            Delete
          </Button>
          <Separator orientation="vertical" size="2" />
          <Button variant="surface" color="gray" onClick={onExit}>
            Done
          </Button>
        </Flex>
        <Box flexGrow="1" />
        <Button
          variant="soft"
          onClick={onClear}
          disabled={!hasSelection || bulkPending}
        >
          Clear
        </Button>
      </Flex>

      <Inset side="x">
        {hasSelection ? (
          <Flex
            align="center"
            gap="2"
            wrap="wrap"
            px="3"
            py="2"
            style={{ borderTop: "1px solid var(--gray-a5)" }}
          >
            <Text size="2" color="gray">
              Selected:
            </Text>
            {selectedEntries.map((entry) => (
              <Badge key={entry.id} size="2" variant="soft" radius="full">
                {entry.title}
                <IconButton
                  size="1"
                  variant="ghost"
                  radius="full"
                  ml="1"
                  onClick={() => onRemoveSelected(entry.id)}
                  aria-label={`Remove ${entry.title} from selection`}
                >
                  <FontAwesome prefix="fas" name="fa-xmark" ariaHidden />
                </IconButton>
              </Badge>
            ))}
          </Flex>
        ) : (
          <Flex
            align="center"
            px="3"
            py="2"
            style={{ borderTop: "1px solid var(--gray-a5)" }}
          >
            <Text size="2" color="gray">
              None selected
            </Text>
          </Flex>
        )}
      </Inset>

      {isMixedSelection && (
        <Inset side="x">
          <Flex
            align="center"
            gap="2"
            px="3"
            py="2"
            style={{
              borderTop: "1px solid var(--gray-a5)",
              backgroundColor: "var(--gray-a2)",
            }}
          >
            <FontAwesome prefix="fas" name="fa-circle-info" ariaHidden />
            <Text size="2" color="gray">
              {publishedSelectedCount} published, {unpublishedSelectedCount}{" "}
              unpublished — Publish and Unpublish apply to all {selectedCount}.
            </Text>
          </Flex>
        </Inset>
      )}
    </Card>
  );
}
